import { useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import servicePages from "../data/servicePages";
import {
  Calculator,
  BookOpen,
  Banknote,
  Landmark,
  ArrowRight,
  CheckCircle2,
  Users,
  Building2,
  TrendingUp,
  SeparatorHorizontal,
} from "lucide-react";

const services = [
  // B2B Section
  {
    id: "b2b",
    title: "B2B : Scale Up Your Firm With Us",
    icon: Users,
    overview: "Enhance Your Practice with Outsourced Support",
    subtitle: "Services for CPAs & Bookkeepers (B2B)",
    mainHeading:
      "Enhance Your Practice with Alpha Accountants Outsourced Support.",
    introText:
      "As a CPA or bookkeeper, balancing client demands with operational efficiency can be challenging. Alpha Accountants offers seamless, reliable support, acting as an extension of your team. We help you scale your practice, manage peak workloads, and expand your service offerings without increasing your overhead. Partner with us to deliver exceptional value to your clients.",
    detailHeading: "Key Offerings for CPAs & Bookkeepers",
    detailSubtext:
      "Comprehensive solutions designed specifically for accounting professionals.",
    cardTitle: "Professional Services for Accounting Firms",
    features: [
      {
        title: "Comprehensive Bookkeeping Support",
        desc: "From daily transaction entry to month-end close and financial statement preparation.",
      },
      {
        title: "Tax Preparation Assistance",
        desc: "Support in gathering data, preparing, and reviewing tax returns for various entities.",
      },
      {
        title: "Payroll Processing",
        desc: "Handling all aspects of payroll, including calculations, filings, and compliance for your clients.",
      },
      {
        title: "Sales Tax Compliance",
        desc: "Expertise in multi-state sales tax calculation, filing, and audit support.",
      },
      {
        title: "Software Integration & Optimization",
        desc: "Assisting with QuickBooks, Xero, and other accounting software setups and troubleshooting.",
      },
    ],
    image: "/images/photos/unsplash-c3cb94fc.webp",
    category: "b2b",
  },
  // B2C Main Section
  {
    id: "b2c",
    title: "B2C : Built For Business Growth",
    icon: Building2,
    overview: "Streamline Your Finances with Expert Support",
    subtitle: "Services for Businesses & Individuals (B2C)",
    mainHeading: "Streamline Your Finances with Alpha Accountants.",
    introText:
      "Whether you're a small business owner navigating complex financial laws or an individual seeking peace of mind with your personal taxes, Alpha Accountants Inc. is here for you. We provide expert, personalized financial services designed to help you save time, reduce stress, and achieve your financial goals. Focus on what you do best, while we handle your books and taxes.",
    detailHeading: "Key Offerings for Businesses & Individuals",
    detailSubtext:
      "Comprehensive financial solutions tailored to your unique needs.",
    cardTitle: "Complete Financial Services Suite",
    features: [
      {
        title: "Bookkeeping Services",
        desc: "Professional management of your daily financial records and reporting.",
      },
      {
        title: "Payroll Services",
        desc: "End-to-end payroll processing and compliance management.",
      },
      {
        title: "Sales Tax Services",
        desc: "Multi-jurisdictional compliance and filing expertise.",
      },
      {
        title: "Tax Preparation & Planning",
        desc: "Strategic tax solutions for individuals and corporations.",
      },
      {
        title: "CFO & Advisory Services",
        desc: "High-level financial strategy and growth consulting.",
      },
    ],
    image: "/images/photos/unsplash-ba37d658.webp",
    category: "b2c",
  },
  // B2C Subtopics - Bookkeeping
  {
    id: "bookkeeping",
    title: "Bookkeeping Services",
    icon: BookOpen,
    overview: "Accurate record-keeping and financial clarity",
    subtitle: "Bookkeeping Services",
    mainHeading: "Detailed Financial Record-Keeping",
    introText:
      "Stay on top of your finances with our comprehensive bookkeeping services. We ensure your records are accurate, up-to-date, and ready for tax season or investor review.",
    detailHeading: "Key Offerings for Bookkeeping",
    detailSubtext:
      "Precise financial tracking to drive informed business decisions.",
    cardTitle: "Bookkeeping Service Offerings",
    features: [
      {
        title: "Detailed Transaction Categorization",
        desc: "Accurate record-keeping for all income and expenses.",
      },
      {
        title: "Bank & Credit Card Reconciliations",
        desc: "Ensuring your accounts are balanced and up-to-date.",
      },
      {
        title: "Accounts Payable & Receivable Management",
        desc: "Efficient management of your cash flow.",
      },
      {
        title: "Financial Statement Preparation",
        desc: "Timely and accurate profit & loss statements, balance sheets, and cash flow reports.",
      },
      {
        title: "Custom Financial Reporting",
        desc: "Providing insights tailored to your business needs.",
      },
    ],
    image: "/images/photos/unsplash-cf6ab908.webp",
    category: "b2c-detail",
  },
  // B2C Subtopics - Payroll
  {
    id: "payroll",
    title: "Payroll Services",
    icon: Banknote,
    overview: "Accurate, timely, and compliant payroll management",
    subtitle: "Payroll Services",
    mainHeading: "Hassle-Free Payroll Processing",
    introText:
      "Pay your team accurately and on time, every time. Our payroll services handle everything from calculations to tax filings, so you can focus on growing your business.",
    detailHeading: "Key Offerings for Payroll",
    detailSubtext: "Complete payroll management for businesses of all sizes.",
    cardTitle: "Payroll Service Offerings",
    features: [
      {
        title: "Employee Payroll Processing",
        desc: "Accurate calculation of wages, deductions, and net pay.",
      },
      {
        title: "Direct Deposit & Check Processing",
        desc: "Convenient payment options for your employees.",
      },
      {
        title: "Payroll Tax Filings",
        desc: "Timely and compliant submission of federal and state payroll taxes (941, 940, state unemployment, etc.).",
      },
      {
        title: "W-2 and 1099 Preparation",
        desc: "Year-end reporting for employees and contractors.",
      },
      {
        title: "Labor Law Compliance",
        desc: "Guidance on payroll regulations and laws.",
      },
    ],
    image: "https://images.pexels.com/photos/6694569/pexels-photo-6694569.jpeg?auto=compress&cs=tinysrgb&w=800&h=800&fit=crop",
    category: "b2c-detail",
  },
  // B2C Subtopics - Sales Tax
  {
    id: "sales-tax",
    title: "Sales Tax Services",
    icon: Landmark,
    overview: "Multi-jurisdictional sales tax compliance",
    subtitle: "Sales Tax Services",
    mainHeading: "Simplify Your Sales Tax Obligations",
    introText:
      "Navigate the complex world of sales tax with confidence. Our experts help you register, file, and stay compliant across all states where you do business.",
    detailHeading: "Key Offerings for Sales Tax",
    detailSubtext:
      "Protect your business from audit risks with our expert guidance.",
    cardTitle: "Sales Tax Service Offerings",
    features: [
      {
        title: "Multi-State Sales Tax Registration",
        desc: "Assisting with acquiring necessary sales tax permits.",
      },
      {
        title: "Sales Tax Calculation & Collection",
        desc: "Ensuring correct rates are applied to transactions.",
      },
      {
        title: "Timely Filing & Remittance",
        desc: "Preparation and submission of regular sales tax returns.",
      },
      {
        title: "Audit Representation",
        desc: "Support and representation during sales tax audits.",
      },
      {
        title: "Nexus Consulting",
        desc: "Determining your sales tax obligations in different states.",
      },
    ],
    image: "/images/photos/unsplash-f246c404.webp",
    category: "b2c-detail",
  },
  // B2C Subtopics - Tax Preparation
  {
    id: "tax-preparation",
    title: "Tax Preparation & Planning",
    icon: Calculator,
    overview: "Maximize deductions and minimize liabilities",
    subtitle: "Tax Preparation & Planning",
    mainHeading: "Strategic Tax Solutions for Every Situation",
    introText:
      "Whether you're filing as an individual or a business, our tax experts ensure accuracy, compliance, and optimal outcomes. We find deductions you might miss and plan ahead for future tax seasons.",
    detailHeading: "Key Offerings for Tax Prep",
    detailSubtext: "Expert tax support for individuals and businesses alike.",
    cardTitle: "Tax Service Offerings",
    features: [
      {
        title: "Individual Tax Returns (1040)",
        desc: "Expert preparation and filing to maximize deductions and credits.",
      },
      {
        title: "Corporate Tax Returns (1120, 1120-S)",
        desc: "Comprehensive tax services for C-corps and S-corps.",
      },
      {
        title: "Partnership Tax Returns (1065)",
        desc: "Accurate reporting for partnerships and LLCs.",
      },
      {
        title: "Tax Planning & Strategy",
        desc: "Proactive advice to minimize tax liabilities and optimize financial outcomes.",
      },
      {
        title: "IRS Audit Representation",
        desc: "Professional support and communication with tax authorities.",
      },
    ],
    image: "/images/photos/unsplash-0665d5cd.webp",
    category: "b2c-detail",
  },
  // B2C Subtopics - CFO & Advisory
  {
    id: "cfo-advisory",
    title: "CFO & Advisory Services",
    icon: TrendingUp,
    overview: "Strategic financial guidance for growth",
    subtitle: "CFO & Advisory Services",
    mainHeading: "Expert Financial Leadership",
    introText:
      "Get high-level financial strategy and insights with our fractional CFO services. We help you make informed decisions, secure funding, and scale sustainably.",
    detailHeading: "Key Offerings for Advisory",
    detailSubtext:
      "Partner with experienced financial leaders to achieve your goals.",
    cardTitle: "CFO & Advisory Service Offerings",
    features: [
      {
        title: "Strategic Financial Planning",
        desc: "Guiding short-term and long-term financial decisions.",
      },
      {
        title: "Budgeting & Forecasting",
        desc: "Developing realistic budgets and financial projections.",
      },
      {
        title: "Cash Flow Management",
        desc: "Optimizing liquidity and ensuring financial stability.",
      },
      {
        title: "Financial Performance Analysis",
        desc: "Interpreting financial data for actionable insights and growth strategies.",
      },
      {
        title: "Business Growth & Expansion Advice",
        desc: "Strategic guidance for scaling your business.",
      },
      {
        title: "Startup Financial Consulting",
        desc: "Setting up robust financial systems for new ventures.",
      },
    ],
    image: "/images/photos/unsplash-cd8de1a0.webp",
    category: "b2c-detail",
  },
  {
  id: "financial-reporting",
  title: "Comprehensive Financial Reporting",
  icon: TrendingUp,
  overview: "Clear, structured, and actionable financial reporting",
  subtitle: "Comprehensive Financial Reporting",
  mainHeading: "Insight-Driven Financial Reporting",
  introText:
    "We provide a wide range of customized financial reports that cater to both operational and strategic needs. These include profit and loss statements with detailed segmentation, balance sheets with enhanced visibility, and cash flow reports that provide clarity on liquidity and financial stability.",
  detailHeading: "Key Reporting Capabilities",
  detailSubtext:
    "Designed to improve visibility, control, and decision-making across your business.",
  cardTitle: "Financial Reporting Services",
  features: [
    {
      title: "Customized Financial Reporting",
      desc: "Tailored reports aligned with operational and strategic business requirements.",
    },
    {
      title: "Profit & Loss Statements",
      desc: "Detailed P&L reporting with segmentation for deeper performance analysis.",
    },
    {
      title: "Balance Sheet Reporting",
      desc: "Enhanced balance sheet structure for clear financial position visibility.",
    },
    {
      title: "Cash Flow Analysis",
      desc: "Comprehensive tracking of inflows and outflows to ensure liquidity stability.",
    },
    {
      title: "Management Reporting",
      desc: "Structured reports to support internal decision-making and performance tracking.",
    },
    {
      title: "Business Insights & Trends",
      desc: "Identification of key trends, risks, and opportunities for strategic growth.",
    },
  ],
  image: "/images/photos/unsplash-b76eeadb.webp",
  category: "b2c-detail",
},
];

const pageSlugById = {
  b2b: "cpa-firm-outsourcing",
  bookkeeping: "bookkeeping",
  payroll: "payroll",
  "sales-tax": "sales-tax",
  "tax-preparation": "tax-preparation",
  "cfo-advisory": "cfo-advisory",
  "financial-reporting": "financial-reporting",
};
const pageNameBySlug = Object.fromEntries(servicePages.map((p) => [p.slug, p.name]));

// Old /services#anchor links pointed at the Expertise page, which now lives at /expertise.
const expertiseAnchors = [
  "operational-insights",
  "management-reporting",
  "strategic-intelligence",
  "profit-loss",
  "cashflow",
  "balance-sheet",
];

const mainCategories = services.filter(
  (s) => s.category === "b2b" || s.category === "b2c",
);
const subTopics = services.filter((s) => s.category === "b2c-detail");
const leftColumnSubTopics = subTopics.filter((_, index) => index % 2 === 0);
const rightColumnSubTopics = subTopics.filter((_, index) => index % 2 === 1);

const b2bServices = services.filter((s) => s.category === "b2b");
const b2cMainServices = services.filter((s) => s.category === "b2c");
const b2cDetailServices = services.filter((s) => s.category === "b2c-detail");

const ServiceOverviewCard = ({ service, index, direction = 0 }) => (
  <motion.div
    key={service.id}
    initial={{ opacity: 0, x: direction }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true, margin: "-50px" }}
    transition={{
      type: "spring",
      stiffness: 100,
      damping: 20,
      delay: index * 0.1,
    }}
    className="group cursor-pointer"
    onClick={() => {
      document
        .getElementById(service.id)
        ?.scrollIntoView({ behavior: "smooth" });
    }}
  >
    <div className="bg-white border border-slate-200 rounded-2xl px-6 py-5 flex items-center gap-5 shadow-sm hover:shadow-xl hover:border-blue-200 transition-all duration-300 hover:-translate-y-1">
      <div className="shrink-0 w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-all duration-300">
        <service.icon className="w-6 h-6 stroke-[1.5]" />
      </div>
      <div className="flex-grow">
        <h4 className="text-lg font-bold text-[#203468] group-hover:text-blue-600 transition-colors">
          {service.title}
        </h4>
        <p className="text-slate-500 text-sm line-clamp-1">
          {service.overview}
        </p>
      </div>
      <div className="shrink-0 text-slate-300 group-hover:text-blue-600 group-hover:translate-x-1 transition-all duration-300">
        <ArrowRight className="w-5 h-5" />
      </div>
    </div>
  </motion.div>
);

export default function Services() {
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = location.hash.replace("#", "");
    if (expertiseAnchors.includes(hash)) {
      navigate(`/expertise#${hash}`, { replace: true });
    }
  }, [location.hash, navigate]);

  return (
    <div className="bg-white min-h-screen pt-28">
      {/* Hero Section */}
      <section className="relative bg-[#203468] text-white py-24 lg:py-32 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="/images/photos/services-hero.webp"
            alt="Corporate Insights"
            className="w-full h-full object-cover mix-blend-luminosity"
            referrerPolicy="no-referrer"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#203468] to-transparent" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <h2 className="text-blue-400 text-sm font-bold uppercase tracking-[0.2em] mb-4">
              Our Services
            </h2>
            <h1 className="font-serif text-5xl lg:text-7xl font-normal leading-tight mb-6">
              Accounting, Tax &amp;{" "}
              <span className="italic text-blue-300">Payroll</span> Services
            </h1>
            <p className="text-lg lg:text-xl text-slate-300 max-w-3xl mx-auto font-light leading-relaxed">
              Comprehensive financial services designed for CPAs, bookkeepers,
              businesses, and individuals. Partner with us for seamless, expert
              support.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Services Overview Grid with Barrier */}
      <section className="py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="text-center mb-12"
          >
            <h2 className="text-blue-600 text-sm font-bold uppercase tracking-[0.2em] mb-2">
              Our Services
            </h2>
            <h3 className="font-serif text-3xl lg:text-4xl text-[#203468] leading-tight">
              Comprehensive solutions for <br />
              <span className="italic text-blue-600">every challenge</span>
            </h3>
          </motion.div>

          {/* Row 1: Main Categories (B2B and B2C) */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-2">
            {mainCategories.map((service, index) => (
              <ServiceOverviewCard
                key={service.id}
                service={service}
                index={index}
                direction={index === 0 ? -100 : 100}
              />
            ))}
          </div>

          {/* Visual Barrier Text */}
          <div className="relative py-12 flex items-center justify-center">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200"></div>
            </div>
            <div className="relative bg-white px-6 ">
              <span className="text-xs font-bold uppercase tracking-[0.3em] text-blue-600 bg-blue-50 px-6 py-2.5 rounded-full border border-blue-100 shadow-sm flex items-center gap-2 py-2">
                <SeparatorHorizontal className="w-4 h-4" />
                Comprehensive Financial Services Suite
                <SeparatorHorizontal className="w-4 h-4" />
              </span>
            </div>
          </div>

          {/* Sub-Topics Two Column Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8">
            <div className="flex flex-col gap-4">
              {leftColumnSubTopics.map((service, index) => (
                <ServiceOverviewCard
                  key={service.id}
                  service={service}
                  index={index}
                  direction={-100}
                />
              ))}
            </div>
            <div className="flex flex-col gap-4">
              {rightColumnSubTopics.map((service, index) => (
                <ServiceOverviewCard
                  key={service.id}
                  service={service}
                  index={index}
                  direction={100}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Deep Dive Sections - B2B and B2C Main Blocks */}
      <section className="bg-white">
        {[...b2bServices, ...b2cMainServices].map((service) => (
          <div
            key={service.id}
            id={service.id}
            className="py-24 md:py-32 border-b border-slate-200 last:border-0"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                className="text-center max-w-4xl mx-auto mb-20 md:mb-24"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#203468] mb-8 leading-tight">
                  <span className="text-[#019BD7] font-bold block mb-4">
                    {service.subtitle}
                  </span>
                  <span className="font-normal">{service.mainHeading}</span>
                </h2>
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
                  {service.introText}
                </p>
              </motion.div>

              <motion.div
                className="bg-slate-50 rounded-[2.5rem] p-8 md:p-12 lg:p-16 border border-slate-100"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="mb-12 text-center">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#203468] mb-6">
                    {service.detailHeading}
                  </h3>
                  <p className="text-slate-600 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                    {service.detailSubtext}
                  </p>
                  {pageSlugById[service.id] && (
                    <Link
                      to={`/services/${pageSlugById[service.id]}`}
                      className="sp-more-link"
                    >
                      Learn more: {pageNameBySlug[pageSlugById[service.id]]}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                  <div className="bg-white rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 flex flex-col h-full">
                    <h4 className="text-xl md:text-2xl font-bold text-[#203468] mb-10">
                      {service.cardTitle}
                    </h4>
                    <ul className="space-y-8">
                      {service.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-5">
                          <div className="mt-1 text-emerald-500 shrink-0 bg-emerald-50 p-1 rounded-full">
                            <CheckCircle2 className="w-6 h-6" />
                          </div>
                          <div>
                            <h5 className="font-bold text-[#203468] text-lg mb-1">
                              {feat.title}:
                            </h5>
                            <p className="text-slate-600 text-base leading-relaxed">
                              {feat.desc}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-center h-full">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full max-w-lg h-auto object-cover rounded-3xl shadow-sm"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        ))}
      </section>

      {/* Deep Dive Sections - B2C Detailed Services */}
      <section className="bg-slate-50">
        {b2cDetailServices.map((service) => (
          <div
            key={service.id}
            id={service.id}
            className="py-24 md:py-32 border-b border-slate-200 last:border-0 bg-white even:bg-slate-50"
          >
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <motion.div
                className="text-center max-w-4xl mx-auto mb-20 md:mb-24"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5 }}
              >
                <h2 className="text-3xl md:text-4xl lg:text-5xl text-[#203468] mb-8 leading-tight">
                  <span className="text-[#019BD7] font-bold block mb-4">
                    {service.subtitle}
                  </span>
                  <span className="font-normal">{service.mainHeading}</span>
                </h2>
                <p className="text-slate-600 text-lg md:text-xl leading-relaxed">
                  {service.introText}
                </p>
              </motion.div>

              <motion.div
                className="bg-white rounded-[2.5rem] p-8 md:p-12 lg:p-16 border border-slate-100 shadow-sm"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <div className="mb-12 text-center">
                  <h3 className="text-2xl md:text-3xl lg:text-4xl font-bold text-[#203468] mb-6">
                    {service.detailHeading}
                  </h3>
                  <p className="text-slate-600 text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
                    {service.detailSubtext}
                  </p>
                  {pageSlugById[service.id] && (
                    <Link
                      to={`/services/${pageSlugById[service.id]}`}
                      className="sp-more-link"
                    >
                      Learn more: {pageNameBySlug[pageSlugById[service.id]]}
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  )}
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
                  <div className="bg-slate-50 rounded-3xl p-8 md:p-12 shadow-sm border border-slate-100 flex flex-col h-full">
                    <h4 className="text-xl md:text-2xl font-bold text-[#203468] mb-10">
                      {service.cardTitle}
                    </h4>
                    <ul className="space-y-8">
                      {service.features.map((feat, i) => (
                        <li key={i} className="flex items-start gap-5">
                          <div className="mt-1 text-emerald-500 shrink-0 bg-emerald-50 p-1 rounded-full">
                            <CheckCircle2 className="w-6 h-6" />
                          </div>
                          <div>
                            <h5 className="font-bold text-[#203468] text-lg mb-1">
                              {feat.title}:
                            </h5>
                            <p className="text-slate-600 text-base leading-relaxed">
                              {feat.desc}
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="flex items-center justify-center h-full">
                    <img
                      src={service.image}
                      alt={service.title}
                      className="w-full max-w-lg h-auto object-cover rounded-3xl shadow-sm"
                      referrerPolicy="no-referrer"
                    />
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        ))}
      </section>

      {/* Footer CTA */}
      <section className="bg-blue-700 py-24 text-center">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-serif text-4xl md:text-5xl text-white mb-8 leading-tight">
              Ready to streamline your financial operations?
            </h2>
            <p className="text-blue-100 text-lg mb-10 font-light max-w-2xl mx-auto">
              Our team of experts is ready to provide tailored solutions for
              your unique business challenges.
            </p>
            <Link
              to="/contact"
              className="inline-block bg-white text-[#203468] px-10 py-4 text-sm font-bold uppercase tracking-wider hover:bg-slate-100 transition-colors shadow-xl rounded-full"
            >
              Contact Our Experts
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
